import { ArrowSwitchIcon, CheckIcon, ImageIcon, StopIcon, TagIcon, TrashIcon } from '@primer/octicons-react';
import {
  countAnswerChoice,
  decideAnswerTypeObjectiveFromKatex,
  extractAnswerFromKatex,
  formatKatexToHtmlString,
} from '@teamturing/katex-utils';
import { editor } from 'monaco-editor';
import React, { ChangeEvent, Suspense, useRef, useState } from 'react';
import { graphql, useFragment } from 'react-relay';

import useToast from '../../../hooks/useToast';
import {
  TaskLabelingProblemUpdateDialog_task$key,
  TaskProblemCreationTypeEnum,
} from '../../../relay/__generated__/TaskLabelingProblemUpdateDialog_task.graphql';
import { TaskLabelingProblemUpdateDialog_taskLabelingProblemUpdateMutation } from '../../../relay/__generated__/TaskLabelingProblemUpdateDialog_taskLabelingProblemUpdateMutation.graphql';
import { UploadMetaDataSchema } from '../../../types/upload';
import { getUploadMetaData, uploadGeneral } from '../../../utils/file';
import { isNullable } from '../../../utils/is';
import { numberWithCommas } from '../../../utils/number';
import { filterCreationTypesByProblemType } from '../../../utils/task';
import {
  createImageRequestMemo,
  formatTaskImageLinkLocation,
  getTaskImageLinks,
  removeImageTag,
  syncImageRequestsWithLinks,
} from '../../../utils/taskImage';
import Button from '../../core/Button';
import Card from '../../core/Card';
import DescriptionList from '../../core/DescriptionList';
import Dialog, { DialogProps } from '../../core/Dialog';
import EmptyState from '../../core/EmptyState';
import EnumPair from '../../core/EnumPair';
import EnumPairSelectField from '../../core/EnumPairSelectField';
import Grid from '../../core/Grid';
import IconButton from '../../core/IconButton';
import Image from '../../core/Image';
import ItemList from '../../core/ItemList';
import { KatexEditorShortcutButtonsStack } from '../../core/KatexEditor';
import KatexEditorField, { KatexEditorFieldProps } from '../../core/KatexEditorField';
import Label from '../../core/Label';
import MutationFormik, { MutationFormikProps } from '../../core/MutationFormik';
import NonFieldError from '../../core/NonFieldError';
import SquareView from '../../core/SquareView';
import Stack from '../../core/Stack';
import StyledOcticon from '../../core/StyledOcticon';
import TabHandler from '../../core/TabHandler';
import Text from '../../core/Text';
import TextareaField from '../../core/TextareaField';
import UploadField from '../../core/UploadField';
import UploadIconButtonField from '../../core/UploadIconButtonField';
import View from '../../core/View';

const TaskLabelingProblemUpdateDialog_task = graphql`
  fragment TaskLabelingProblemUpdateDialog_task on Task {
    id
    title
    sequence
    status
    problem {
      id
      problem
      solution
      problemType
      creationType
      answer
      answerChoiceCount
      answerType
    }

    scrap {
      id
      problems {
        id
        objectUrl
      }
      solutions {
        id
        objectUrl
      }
    }

    imageRequests {
      edges {
        node {
          id
          image {
            key
            objectUrl
            size
          }
          description
        }
      }
    }
    aiFile {
      id
      key
      objectUrl
      size
    }
  }
`;

type Props = {
  task: TaskLabelingProblemUpdateDialog_task$key;
  /**
   * 마이그레이션 짝 반영처럼 다른 문제의 내용으로 채워서 열 때 사용합니다.
   */
  initialProblem?: { problem: string; solution: string; creationType?: TaskProblemCreationTypeEnum | null };
  /**
   * 마이그레이션 짝 반영처럼 다른 문제의 ai 파일로 채워서 열 때 사용합니다.
   */
  initialAiFile?: { key: string; size: number; objectUrl: string };
} & DialogProps &
  Pick<
    MutationFormikProps<TaskLabelingProblemUpdateDialog_taskLabelingProblemUpdateMutation>,
    'config' | 'connections' | 'onSubmit'
  >;

const TaskLabelingProblemUpdateDialog = ({
  task,
  initialProblem,
  initialAiFile,
  config,
  connections,
  onSubmit,
  ...props
}: Props) => {
  const {
    id,
    sequence,
    status,
    title,
    problem: taskProblem,
    scrap,
    imageRequests,
    aiFile,
  } = useFragment(TaskLabelingProblemUpdateDialog_task, task);
  const problem = taskProblem && initialProblem ? { ...taskProblem, ...initialProblem } : taskProblem;
  const { toast } = useToast();

  const sideTabItems = [
    { id: 'tag', icon: TagIcon },
    { id: 'image', icon: ImageIcon },
  ];
  const editorInputTabItems = [
    { id: 'all', label: '문제 + 해설' },
    { id: 'problem', label: '문제' },
    { id: 'solution', label: '해설' },
  ];

  const handleChangeUploadField = async (
    e: ChangeEvent<HTMLInputElement>,
    onGeneralUpload: (blob: Blob, metaData: UploadMetaDataSchema) => void,
  ) => {
    const targetFile = e.target.files?.[0];

    if (isNullable(targetFile)) return;
    const { name: fileName, size: fileSize, type: fileType } = targetFile;
    const metaData = await getUploadMetaData({ fileName, fileSize, fileType });

    if (!metaData) {
      return;
    }

    const isMultiPartUploadRequired = 'upload_id' in metaData && 'part_metadata' in metaData;

    if (isMultiPartUploadRequired) {
      toast('사진 크기가 너무 큽니다. 작은 사진을 첨부해주세요', 'error');
    } else {
      const response = await uploadGeneral(targetFile, metaData);
      if (response.ok) {
        onGeneralUpload(targetFile, metaData);
      }
    }
  };

  const problemEditorRef = useRef<editor.IStandaloneCodeEditor>(null);
  const solutionEditorRef = useRef<editor.IStandaloneCodeEditor>(null);
  const [activeEditor, setActiveEditor] = useState<editor.IStandaloneCodeEditor | null>(null);
  const imageRequestMemo = useRef(createImageRequestMemo(imageRequests.edges.map(({ node }) => node)));

  const isFinalLabeling = status === 'review';

  const getAnswerChoiceCountByAnswerType = (value: string) =>
    decideAnswerTypeObjectiveFromKatex(value) ? countAnswerChoice(value) || 5 : null;
  const getAnswerType = (value: string) => (decideAnswerTypeObjectiveFromKatex(value) ? 'objective' : 'subjective');

  return (
    <Dialog {...props}>
      <TabHandler initialSelectIndex={0}>
        {({ selectedIndex: selectedSideTabIndex, handleSelect: handleSelectSideTab }) => (
          <MutationFormik<TaskLabelingProblemUpdateDialog_taskLabelingProblemUpdateMutation>
            mutation={graphql`
              mutation TaskLabelingProblemUpdateDialog_taskLabelingProblemUpdateMutation(
                $input: TaskLabelingProblemUpdateInput!
              ) {
                taskLabelingProblemUpdate(input: $input) {
                  id
                  ...TaskLabelingProblemUpdateDialog_task
                }
              }
            `}
            initialValues={{
              answer: extractAnswerFromKatex(problem?.solution || '') || '',
              answerChoiceCount: getAnswerChoiceCountByAnswerType(problem?.problem || ''),
              answerType: getAnswerType(problem?.problem || ''),
              problem: {
                text: problem?.problem || '',
                textHtml: formatKatexToHtmlString(problem?.problem || ''),
              },
              solution: {
                text: problem?.solution || '',
                textHtml: formatKatexToHtmlString(problem?.solution || ''),
              },
              task: id,
              taskProblem: problem?.id || '',
              creationType: problem?.creationType || ('' as TaskProblemCreationTypeEnum),
              aiFile:
                initialAiFile ??
                (aiFile ? { key: aiFile.key, size: aiFile.size || 0, objectUrl: aiFile?.objectUrl } : null),
              imageRequests: syncImageRequestsWithLinks(
                problem?.problem || '',
                problem?.solution || '',
                [],
                imageRequestMemo.current,
              ),
            }}
            config={{
              ...config,
              onError: (...arg) => {
                config?.onError?.(...arg);
                handleSelectSideTab(1);
              },
            }}
            onSubmit={onSubmit}
            connections={connections}
          >
            {({ dirty, handleSubmit, setFieldValue, values }, { scrollContainerRef, nonFieldErrorRef }) => {
              const handleChangeProblemKatexEditor: KatexEditorFieldProps['onChange'] = (value = '') => {
                setFieldValue('problem', { text: value, textHtml: formatKatexToHtmlString(value) });
                setFieldValue('answerType', getAnswerType(value || ''));
                setFieldValue('answerChoiceCount', getAnswerChoiceCountByAnswerType(value || ''));
                setFieldValue(
                  'imageRequests',
                  syncImageRequestsWithLinks(
                    value,
                    values.solution.text,
                    values.imageRequests || [],
                    imageRequestMemo.current,
                  ),
                );
              };
              const handleChangeSolutionKatexEditor: KatexEditorFieldProps['onChange'] = (value = '') => {
                setFieldValue('solution', { text: value, textHtml: formatKatexToHtmlString(value) });
                setFieldValue('answer', extractAnswerFromKatex(value || ''));
                setFieldValue(
                  'imageRequests',
                  syncImageRequestsWithLinks(
                    values.problem.text,
                    value,
                    values.imageRequests || [],
                    imageRequestMemo.current,
                  ),
                );
              };

              // 편집기에 링크가 들어가면 onChange에서 목록에 올라간다. 여기서는 파일 정보만 남겨 둔다
              const handleUploadImage: KatexEditorFieldProps['onUploadImage'] = (blob, metadata) => {
                imageRequestMemo.current.set(metadata.key, {
                  file: { key: metadata.key, size: blob.size, objectUrl: metadata.object_url },
                  description: '',
                });
                handleSelectSideTab(1);
              };

              // 이미지를 지우면 문제·해설의 링크도 지운다. 메모는 남겨 두어 같은 링크를 다시 넣으면 다시 붙는다
              const handleClickTrashButton = (index: number) => {
                const imageSrc = values.imageRequests?.[index].file.objectUrl || '';
                const problem = removeImageTag(values.problem.text, imageSrc);
                const solution = removeImageTag(values.solution.text, imageSrc);
                problemEditorRef.current?.setValue(problem);
                solutionEditorRef.current?.setValue(solution);
                setFieldValue('problem', { text: problem, textHtml: formatKatexToHtmlString(problem) });
                setFieldValue('solution', { text: solution, textHtml: formatKatexToHtmlString(solution) });
                setFieldValue(
                  'imageRequests',
                  syncImageRequestsWithLinks(problem, solution, values.imageRequests || [], imageRequestMemo.current),
                );
              };

              const imageLinks = getTaskImageLinks(values.problem.text, values.solution.text);

              const handleGeneralUploadImage = (index: number) => (blob: Blob, metaData: UploadMetaDataSchema) => {
                const previousImageSrc = values.imageRequests?.[index].file.objectUrl;
                if (!previousImageSrc) return;

                const nextImageSrc = metaData.object_url;
                // 교체한 이미지에도 같은 메모가 붙도록 새 주소로 남겨 둔다
                imageRequestMemo.current.set(metaData.key, {
                  file: { key: metaData.key, size: blob.size, objectUrl: nextImageSrc },
                  description: values.imageRequests?.[index].description || '',
                });

                const problem = values.problem.text.replaceAll(previousImageSrc, nextImageSrc);
                const solution = values.solution.text.replaceAll(previousImageSrc, nextImageSrc);

                problemEditorRef.current?.setValue(problem);
                solutionEditorRef.current?.setValue(solution);
                setFieldValue(`imageRequests.${index}.file`, {
                  key: metaData.key,
                  size: blob.size,
                  objectUrl: metaData.object_url,
                });
              };

              return (
                <>
                  <Dialog.Header>
                    <Grid sx={{ paddingRight: 4, alignItems: 'center' }}>
                      <Grid.Unit size={'max'}>
                        <Stack gapX={1}>
                          <Stack.Item>
                            <Text sx={{ fontSize: 3, fontWeight: 'bold' }}>수정하기</Text>
                          </Stack.Item>
                          {problem ? (
                            <Stack.Item>
                              <Suspense>
                                <Label variant={'accent'}>
                                  <EnumPair typename={'TaskProblemTypeEnum'}>{problem.problemType}</EnumPair>
                                  {` · ${title}`}
                                </Label>
                              </Suspense>
                            </Stack.Item>
                          ) : null}
                          <Stack.Item>
                            <Label variant={'primary'}>{sequence}</Label>
                          </Stack.Item>
                        </Stack>
                      </Grid.Unit>
                      <Grid.Unit size={'min'}>
                        <Stack gapX={1}>
                          <ItemList
                            items={sideTabItems}
                            renderItem={({ icon }, index) => (
                              <IconButton
                                onClick={() => handleSelectSideTab(index)}
                                variant={selectedSideTabIndex === index ? 'default' : 'plain'}
                                icon={icon}
                                aria-label={`open ${id} tab`}
                              />
                            )}
                            renderItemWrapper={(children, { id }) => <Stack.Item key={id}>{children}</Stack.Item>}
                          />
                          <Stack.Item>
                            <Button
                              variant={'primary'}
                              onClick={() => handleSubmit()}
                              leadingIcon={CheckIcon}
                              // 짝 반영으로 열면 반영할 내용이 초기값이라 수정 없이도 저장할 수 있어야 합니다
                              disabled={!dirty && !initialProblem}
                            >
                              저장하기
                            </Button>
                          </Stack.Item>
                        </Stack>
                      </Grid.Unit>
                    </Grid>
                  </Dialog.Header>
                  <Grid wrap={false} sx={{ height: '100%', overflowY: 'hidden' }}>
                    <Grid.Unit size={3 / 4}>
                      <TabHandler initialSelectIndex={0}>
                        {({ selectedIndex: selectedEditorInputTab, handleSelect: handleSelectEditorInputTab }) => (
                          <View
                            ref={scrollContainerRef}
                            sx={{
                              'padding': 3,
                              'height': '100%',
                              'overflowY': 'auto',
                              '&>*:not(:last-child)': { marginBottom: 5 },
                            }}
                          >
                            <NonFieldError ref={nonFieldErrorRef} />
                            <Grid gapY={2}>
                              <Grid.Unit size={'max'}>
                                <Stack gapX={1}>
                                  <ItemList
                                    items={editorInputTabItems}
                                    renderItem={({ label }, index) => (
                                      <Button
                                        onClick={() => handleSelectEditorInputTab(index)}
                                        variant={selectedEditorInputTab === index ? 'outline' : 'invisible'}
                                      >
                                        {label}
                                      </Button>
                                    )}
                                    renderItemWrapper={(children, { id }) => (
                                      <Stack.Item key={id}>{children}</Stack.Item>
                                    )}
                                  />
                                </Stack>
                              </Grid.Unit>
                              <Grid.Unit size={'min'}>
                                <KatexEditorShortcutButtonsStack editor={activeEditor!} gapX={1} />
                              </Grid.Unit>
                            </Grid>
                            <View sx={{ '&>*:not(:first-child)': { marginTop: 3 } }}>
                              {editorInputTabItems[selectedEditorInputTab].id === 'all' ||
                              editorInputTabItems[selectedEditorInputTab].id === 'problem' ? (
                                <Grid gapX={2}>
                                  <Grid.Unit size={2 / 3}>
                                    <KatexEditorField
                                      ref={problemEditorRef}
                                      name={'problem'}
                                      label={'문제'}
                                      type={'scroll'}
                                      height={
                                        editorInputTabItems[selectedEditorInputTab].id === 'all' ? '33vh' : '70vh'
                                      }
                                      required
                                      onFocus={setActiveEditor}
                                      onUploadImage={handleUploadImage}
                                      onChange={handleChangeProblemKatexEditor}
                                      emptyStateText={'아직 타이핑한 문제가 없어요'}
                                    />
                                  </Grid.Unit>
                                  <Grid.Unit size={1 / 3} sx={{ '&>*:not(:first-child)': { marginTop: 1 } }}>
                                    <Text fontSize={1}>스크랩한 문제</Text>
                                    <Card
                                      sx={{
                                        overflow: 'auto',
                                        height:
                                          editorInputTabItems[selectedEditorInputTab].id === 'all' ? '33vh' : '70vh',
                                      }}
                                    >
                                      <ItemList
                                        items={scrap?.problems || []}
                                        renderItem={({ objectUrl }) => (
                                          <Image
                                            key={objectUrl}
                                            src={objectUrl}
                                            alt={objectUrl}
                                            html
                                            style={{ display: 'block' }}
                                          />
                                        )}
                                      />
                                    </Card>
                                  </Grid.Unit>
                                </Grid>
                              ) : null}
                              {editorInputTabItems[selectedEditorInputTab].id === 'all' ||
                              editorInputTabItems[selectedEditorInputTab].id === 'solution' ? (
                                <Grid gapX={2}>
                                  <Grid.Unit size={2 / 3}>
                                    <KatexEditorField
                                      ref={solutionEditorRef}
                                      name={'solution'}
                                      label={'해설'}
                                      type={'scroll'}
                                      height={
                                        editorInputTabItems[selectedEditorInputTab].id === 'all' ? '33vh' : '70vh'
                                      }
                                      required
                                      onFocus={setActiveEditor}
                                      onUploadImage={handleUploadImage}
                                      onChange={handleChangeSolutionKatexEditor}
                                      emptyStateText={'아직 타이핑한 해설이 없어요'}
                                    />
                                  </Grid.Unit>
                                  <Grid.Unit size={1 / 3} sx={{ '&>*:not(:first-child)': { marginTop: 1 } }}>
                                    <Text fontSize={1}>스크랩한 해설</Text>
                                    <Card
                                      sx={{
                                        overflow: 'auto',
                                        height:
                                          editorInputTabItems[selectedEditorInputTab].id === 'all' ? '33vh' : '70vh',
                                      }}
                                    >
                                      <ItemList
                                        items={scrap?.solutions || []}
                                        renderItem={({ objectUrl }) => (
                                          <Image
                                            key={objectUrl}
                                            src={objectUrl}
                                            alt={objectUrl}
                                            html
                                            style={{ display: 'block' }}
                                          />
                                        )}
                                      />
                                    </Card>
                                  </Grid.Unit>
                                </Grid>
                              ) : null}
                            </View>
                          </View>
                        )}
                      </TabHandler>
                    </Grid.Unit>
                    <Grid.Unit size={1 / 4}>
                      <View
                        sx={{
                          'padding': 3,
                          'backgroundColor': 'canvas.inset',
                          'overflowY': 'auto',
                          'height': '100%',
                          '&>*:not(:first-child)': { marginTop: 4 },
                        }}
                      >
                        {sideTabItems[selectedSideTabIndex].id === 'tag' ? (
                          <>
                            <View>
                              <Text fontSize={2} fontWeight={'bold'}>
                                문제 정보
                              </Text>
                            </View>
                            <Card sx={{ padding: 3, backgroundColor: 'canvas.inset' }}>
                              <DescriptionList
                                item={values}
                                itemDescriptions={{
                                  answerType: {
                                    title: '문제 유형',
                                    renderValue: ({ answerType }) =>
                                      answerType ? (
                                        <Suspense>
                                          <EnumPair typename={'TaskProblemAnswerTypeEnum'}>{answerType}</EnumPair>
                                        </Suspense>
                                      ) : undefined,
                                  },
                                  answer: {
                                    title: '정답',
                                    renderValue: ({ answer }) =>
                                      isNullable(answer) || answer === '' ? (
                                        <View
                                          sx={{
                                            color: 'danger.fg',
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                          }}
                                        >
                                          <Stack gapX={1}>
                                            <Stack.Item>
                                              <StyledOcticon icon={StopIcon} size={12} />
                                            </Stack.Item>
                                            <Stack.Item>
                                              <Text fontSize={1}>입력 필요</Text>
                                            </Stack.Item>
                                          </Stack>
                                        </View>
                                      ) : (
                                        answer
                                      ),
                                  },
                                  answerChoiceCount: {
                                    title: '선지 개수',
                                    renderValue: ({ answerChoiceCount }) =>
                                      answerChoiceCount !== 0 ? answerChoiceCount : undefined,
                                  },
                                }}
                                picks={['answerType', 'answer', 'answerChoiceCount']}
                                titleUnitSize={1 / 2}
                                descriptionUnitSize={1 / 2}
                              />
                            </Card>
                            {isFinalLabeling ? (
                              <Suspense>
                                <EnumPairSelectField
                                  typename={'TaskProblemCreationTypeEnum'}
                                  transformer={(enumPairs) =>
                                    filterCreationTypesByProblemType(enumPairs, problem?.problemType)
                                  }
                                  label={'변형 정도'}
                                  name={'creationType'}
                                  placeholder={'변형 정도 선택'}
                                  required
                                />
                              </Suspense>
                            ) : null}
                          </>
                        ) : sideTabItems[selectedSideTabIndex].id === 'image' ? (
                          <>
                            <Stack gapX={1}>
                              <Stack.Item>
                                <Text fontSize={2} fontWeight={'bold'}>
                                  요청한 이미지
                                </Text>
                              </Stack.Item>
                              <Stack.Item>
                                <Text
                                  sx={{ fontSize: 1, fontWeight: 'bold' }}
                                  color={
                                    (values.imageRequests?.length || 0) === 0 ? 'neutral.emphasis' : 'accent.emphasis'
                                  }
                                >
                                  {numberWithCommas(values.imageRequests?.length || 0)}
                                </Text>
                              </Stack.Item>
                            </Stack>
                            <UploadField
                              label={'이미지 ai 파일'}
                              name={'aiFile'}
                              placeholder={'ai 파일을 선택하세요'}
                              accept={'application/postscript'}
                              required
                            />
                            <ItemList
                              items={values.imageRequests || []}
                              renderItem={({ file }, index) => (
                                <View sx={{ '&>*:not(:first-child)': { marginTop: 1 } }}>
                                  {imageLinks[index] ? (
                                    <Text fontSize={1} color={'fg.muted'}>
                                      {formatTaskImageLinkLocation(imageLinks[index])}
                                    </Text>
                                  ) : null}
                                  <Card>
                                    <SquareView
                                      sx={{
                                        '&:hover': {
                                          '*': {
                                            display: 'block',
                                          },
                                        },
                                      }}
                                    >
                                      <View
                                        sx={{
                                          position: 'absolute',
                                          display: 'none',
                                          width: '100%',
                                          height: '100%',
                                          backgroundColor: 'fg.default',
                                          opacity: 0.2,
                                          zIndex: 1,
                                        }}
                                      />
                                      <UploadIconButtonField
                                        sx={{ position: 'absolute', display: 'none', left: 2, bottom: 2, zIndex: 1 }}
                                        name={`imageRequest.${index}.file`}
                                        label={`image${index}`}
                                        labelConfig={{ visuallyHidden: true }}
                                        size={'large'}
                                        aria-label={`Change image ${index}`}
                                        icon={ArrowSwitchIcon}
                                        accept={'image/*'}
                                        onChange={(e) => handleChangeUploadField(e, handleGeneralUploadImage(index))}
                                      />
                                      <IconButton
                                        sx={{ position: 'absolute', display: 'none', right: 2, bottom: 2, zIndex: 1 }}
                                        size={'large'}
                                        icon={TrashIcon}
                                        aria-label={`delete image${index}`}
                                        onClick={() => handleClickTrashButton(index)}
                                      />
                                      <Image
                                        src={file.objectUrl || ''}
                                        alt={`requested image ${index}`}
                                        fill
                                        style={{ objectFit: 'contain' }}
                                      />
                                    </SquareView>
                                  </Card>
                                  <TextareaField
                                    id={file.key}
                                    label={'요청사항'}
                                    name={`imageRequests.${index}.description`}
                                    placeholder={'요청사항 입력'}
                                  />
                                </View>
                              )}
                              renderItemWrapper={(children, { file }) => <View key={file.key}>{children}</View>}
                              emptyState={
                                <Card sx={{ paddingY: 5, backgroundColor: 'canvas.inset' }}>
                                  <EmptyState title={'요청한 이미지가 없습니다.'} />
                                </Card>
                              }
                            />
                          </>
                        ) : null}
                      </View>
                    </Grid.Unit>
                  </Grid>
                </>
              );
            }}
          </MutationFormik>
        )}
      </TabHandler>
    </Dialog>
  );
};

export default TaskLabelingProblemUpdateDialog;
